import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, switchMap, tap } from 'rxjs';

export type ProfileImageKind = 'avatar' | 'banner';

/** Resposta de GET /user/me/profile. avatar e banner chegam como data URL. */
export interface UserProfile {
	userId: string;
	firstName?: string;
	lastName?: string;
	email?: string;
	jobTitle: string | null;
	areaId: string | null;
	areaName: string | null;
	unitName: string | null;
	avatar: string | null;
	banner: string | null;
	updatedAt: string | null;
}

/** Tamanho maximo gravado: a imagem e reduzida no navegador antes do upload. */
const IMAGE_LIMITS: Record<ProfileImageKind, { width: number; height: number; crop: boolean }> = {
	avatar: { width: 320, height: 320, crop: true },
	banner: { width: 1600, height: 480, crop: true },
};

/**
 * Perfil do usuario logado (foto, banner, cargo, area). O nome mora no Keycloak;
 * o resto em pmo.UserProfile. profile$ alimenta o menu do usuario e o bloco de
 * identificacao da barra lateral, entao toda alteracao recarrega o perfil.
 */
@Injectable({ providedIn: 'root' })
export class UserProfileService {
	private readonly _profile = new BehaviorSubject<UserProfile | null>(null);
	readonly profile$ = this._profile.asObservable();

	private readonly endpoint = `${environment.url}user/me/profile`;

	constructor(private readonly httpClient: HttpClient) {}

	load(): Observable<UserProfile> {
		return this.httpClient
			.get<UserProfile>(this.endpoint)
			.pipe(tap((profile) => this._profile.next(profile)));
	}

	update(data: { firstName?: string; lastName?: string; jobTitle?: string | null; areaId?: string | null }): Observable<UserProfile> {
		return this.httpClient.put(this.endpoint, data).pipe(switchMap(() => this.load()));
	}

	uploadImage(kind: ProfileImageKind, file: File): Observable<UserProfile> {
		return new Observable<Blob>((subscriber) => {
			resizeImage(file, IMAGE_LIMITS[kind])
				.then((blob) => {
					subscriber.next(blob);
					subscriber.complete();
				})
				.catch((error) => subscriber.error(error));
		}).pipe(
			switchMap((blob) => {
				const body = new FormData();
				body.append('file', blob, `${kind}.jpg`);
				return this.httpClient.post(`${this.endpoint}/${kind}`, body);
			}),
			switchMap(() => this.load()),
		);
	}

	removeImage(kind: ProfileImageKind): Observable<UserProfile> {
		return this.httpClient.delete(`${this.endpoint}/${kind}`).pipe(switchMap(() => this.load()));
	}
}

/**
 * Reduz e recorta (centro) a imagem num canvas e devolve JPEG. Foto de celular tem
 * 4-8 MB; o que vai para o banco fica na casa das dezenas de KB.
 */
function resizeImage(file: File, limit: { width: number; height: number; crop: boolean }): Promise<Blob> {
	return new Promise((resolve, reject) => {
		const url = URL.createObjectURL(file);
		const img = new Image();
		img.onload = () => {
			URL.revokeObjectURL(url);
			const targetRatio = limit.width / limit.height;
			let sx = 0;
			let sy = 0;
			let sw = img.naturalWidth;
			let sh = img.naturalHeight;
			if (limit.crop) {
				if (sw / sh > targetRatio) {
					sw = Math.round(sh * targetRatio);
					sx = Math.round((img.naturalWidth - sw) / 2);
				} else {
					sh = Math.round(sw / targetRatio);
					sy = Math.round((img.naturalHeight - sh) / 2);
				}
			}
			const scale = Math.min(1, limit.width / sw);
			const canvas = document.createElement('canvas');
			canvas.width = Math.round(sw * scale);
			canvas.height = Math.round(sh * scale);
			const ctx = canvas.getContext('2d');
			ctx.fillStyle = '#fff'; // PNG transparente vira fundo branco no JPEG
			ctx.fillRect(0, 0, canvas.width, canvas.height);
			ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
			canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Falha ao processar a imagem'))), 'image/jpeg', 0.85);
		};
		img.onerror = () => {
			URL.revokeObjectURL(url);
			reject(new Error('Arquivo nao e uma imagem valida'));
		};
		img.src = url;
	});
}
